import React, { useState, useEffect } from "react";
import api from "../../services/Api.js";
import {
  FaTrash,
  FaPaperclip,
  FaMicrophone,
  FaRegFileImage,
  FaPaperPlane,
  FaTimes,
} from "react-icons/fa";
import { IoIosArrowBack } from "react-icons/io";
import { AiFillPrinter } from "react-icons/ai";
import { CiStar } from "react-icons/ci";
import { BsThreeDotsVertical } from "react-icons/bs";

const Modal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-lg w-full max-w-md p-6 mx-4">
        {children}
      </div>
    </div>
  );
};

const ChatInterface = () => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Hello! How are you doing?",
      sender: "receiver",
      time: "10:30 AM",
      name: "John Doe",
      imgSrc: "/api/placeholder/32/32",
    },
    {
      id: 2,
      text: "I'm doing great, thanks for asking!",
      sender: "self",
      time: "10:32 AM",
      name: "Me",
      imgSrc: "/api/placeholder/32/32",
    },
    {
      id: 3,
      text: "That's wonderful to hear. Shall we discuss the project?",
      sender: "receiver",
      time: "10:33 AM",
      name: "John Doe",
      imgSrc: "/api/placeholder/32/32",
    },
  ]);
  const [newMessage, setNewMessage] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [emailData, setEmailData] = useState({
    sender: "",
    receiver: "",
    subject: "",
  });
  const [errors, setErrors] = useState({});
  const [users, setUsers] = useState([]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await api.get("/admin/users");
        console.log("API Response:", response.data);
        setUsers(response.data.users || []);
      } catch (error) {
        console.error("Error fetching users:", error);
        setUsers([]); // Fallback to empty array on error
      }
    };
    fetchUsers();
  }, []);

  const validateForm = () => {
    const newErrors = {};

    if (!emailData.receiver) {
      newErrors.receiver = "Please select a user";
    }

    if (!emailData.subject?.trim()) {
      newErrors.subject = "Subject is required";
    }

    if (!newMessage?.trim()) {
      newErrors.message = "Message is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSendClick = () => {
    if (!newMessage?.trim()) {
      setErrors({ ...errors, message: "Message is required" });
      return;
    }
    setShowModal(true);
  };

  const handleSendEmail = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const newMsg = {
        id: messages.length + 1,
        text: newMessage,
        sender: "self",
        time: new Date().toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
        }),
        name: "Me",
        imgSrc: "/api/placeholder/32/32",
      };
      setMessages([...messages, newMsg]);

      const response = await api.post("/emails", {
        body: JSON.stringify({
          sender: emailData.sender,
          receiver: emailData.receiver,
          subject: emailData.subject,
          message: newMessage,
          time: newMsg.time,
        }),
      });

      if (response.ok) {
        setNewMessage("");
        setEmailData({
          sender: "",
          receiver: "",
          subject: "",
        });
        setShowModal(false);
      }
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  return (
    <div className="flex flex-col h-screen bg-white">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <div className="flex items-center gap-2">
          <button className="text-sm bg-gray-200 pl-[2px] text-gray-600 w-5 h-5">
            <IoIosArrowBack />
          </button>
          <span className="font-medium font-sans text-[#202224] text-base">
            Chat
          </span>
        </div>
        <div className="flex border p-2 rounded-lg gap-4">
          <button>
            <AiFillPrinter />
          </button>
          <button>
            <CiStar />
          </button>
          <button>
            <FaTrash />
          </button>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${
              message.sender === "self" ? "justify-end" : "justify-start"
            } items-end gap-2`}
          >
            {message.sender !== "self" && (
              <img
                src={message.imgSrc}
                alt={message.name}
                className="w-8 h-8 rounded-full"
              />
            )}
            <div
              className={`max-w-[70%] rounded-t-xl ${
                message.sender === "self"
                  ? "bg-black text-white rounded-bl-xl"
                  : "bg-gray-100 text-black rounded-br-xl"
              } p-4`}
            >
              <p className="text-sm mb-2">{message.text}</p>
              <div className="flex items-center justify-between text-xs">
                <span>{message.name}</span>
                <span className="flex items-center gap-1">
                  {message.time}
                  <BsThreeDotsVertical />
                </span>
              </div>
            </div>
            {message.sender === "self" && (
              <img
                src={message.imgSrc}
                alt={message.name}
                className="w-8 h-8 rounded-full"
              />
            )}
          </div>
        ))}
      </div>

      {/* Input Area */}
      <div className="border-t p-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center flex-1 bg-gray-50 rounded-lg p-3">
            <FaMicrophone className="text-gray-400 mx-2" />
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Write a message"
              className="flex-1 bg-transparent outline-none px-2"
            />
            <FaPaperclip className="text-gray-400 mx-2" />
            <FaRegFileImage className="text-gray-400 mx-2" />
          </div>
          <button
            onClick={handleSendClick}
            className="bg-black text-white px-4 py-3 rounded-lg flex items-center gap-2"
          >
            Send
            <FaPaperPlane />
          </button>
        </div>
      </div>

      {/* Custom Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Complete Email Details</h2>
          <button
            onClick={() => setShowModal(false)}
            className="text-gray-500 hover:text-gray-700"
          >
            <FaTimes />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <select
              value={emailData.receiver}
              onChange={(e) =>
                setEmailData({ ...emailData, receiver: e.target.value })
              }
              className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-black"
            >
              <option value="" >Select a user</option>
              {Array.isArray(users) &&
                users.map((user) => (
                  <option key={user.id} className="bg-white" value={user.id}>
                    {user.firstName} {user.lastName}
                  </option>
                ))}
            </select>

            {errors.sender && (
              <p className="text-red-500 text-sm mt-1">{errors.sender}</p>
            )}
          </div>

          <div>
            <input
              type="email"
              value={emailData.receiver}
              onChange={(e) =>
                setEmailData({ ...emailData, receiver: e.target.value })
              }
              placeholder="To (recipient's email)"
              className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-black"
            />
            {errors.receiver && (
              <p className="text-red-500 text-sm mt-1">{errors.receiver}</p>
            )}
          </div>

          <div>
            <input
              type="text"
              value={emailData.subject}
              onChange={(e) =>
                setEmailData({ ...emailData, subject: e.target.value })
              }
              placeholder="Subject"
              className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-black"
            />
            {errors.subject && (
              <p className="text-red-500 text-sm mt-1">{errors.subject}</p>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={() => setShowModal(false)}
            className="px-4 py-2 border rounded hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSendEmail}
            className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800"
          >
            Send Email
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default ChatInterface;
